import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { ChevronDown16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

// component functions
import * as ComponentFunctions from './StageFix.Form.Functions';
import { summarizeStages } from './StageFix.Form.Logic';

// models
import { ContractStage } from '../../../../models/Entities/Contracts/ContractStage';
import { ContractBranch } from '../../../../models/Entities/Contracts/ContractBranch';

// components
import StageFixFormItemComponent from './StageFixFormItem/StageFix.Form.Item.Component';
import { GuideDrawer } from '../../../../theme/components/GuideDrawer';

import './StageFix.Form.Styles.css';

const UNASSIGNED_BRANCH_KEY = 'unassigned';

interface BranchGroup {
    key: string;
    branchId?: number;
    label: string;
    stages: ContractStage[];
}

/**
 * Qué visita del flowchart de la historia clínica responde a cada etapa del
 * contrato. Sin esa asociación, las visitas que se cargan en HC no generan
 * consumos ni se liquidan.
 */
const StageFixFormComponent = (props: ComponentFunctions.Props) => {

    const { t } = useTranslation();
    // Ramas plegadas por el usuario; el resto arranca abierto.
    const [collapsed, setCollapsed] = useState<string[]>([]);

    //load necesary data
    useEffect(() => {
        props.Protocol?.id && props.GetMedicalProtocolById(props.Protocol?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.Protocol?.id === undefined]);

    // El drawer queda montado aunque esté cerrado: se relee cada vez que se
    // abre, así refleja lo que se asoció desde el contrato mientras tanto.
    useEffect(() => {
        if (!props.isOpen) return;
        props.GetVisitFullByProtocolId(props.ProtocolId);
        props.Contract?.id && props.GetAllContractsStage(props.Contract.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.ProtocolId]);

    // branches used to group stages/visits
    useEffect(() => {
        if (props.Protocol?.contractAssociation?.contractId) {
            props.GetContractById(props.Protocol.contractAssociation.contractId);
            props.GetAllContractsBranchs(props.Protocol.contractAssociation.contractId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.Protocol?.contractAssociation?.contractId]);

    // Contract.stages' reducer no-ops unless the Contract already exists in state, so this must wait for GetContractById above to land.
    useEffect(() => {
        props.Contract?.id && props.GetAllContractsStage(props.Contract.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.Contract?.id]);

    const HandleCancelForm = () => {
        props.dismissPanel();
    };

    const contractBranches = useMemo(() => {
        const activeAddendum = props.Contract?.addendums?.find((addendum) => addendum.isActive)
            || props.Contract?.addendums?.[0];
        const activeAddendumId = activeAddendum?.id;

        const branchesFromActiveAddendum = activeAddendum?.branches || [];
        const branchesFromContract = activeAddendumId
            ? (props.Contract?.branches || []).filter((branch) => branch.contractAddendumId === activeAddendumId)
            : [];
        const branchesFromStore = activeAddendumId
            ? (props.ContractBranches || []).filter((branch) => branch.contractAddendumId === activeAddendumId)
            : (props.ContractBranches || []);

        return [...branchesFromActiveAddendum, ...branchesFromContract, ...branchesFromStore]
            .filter((branch, index, array) =>
                index === array.findIndex((candidate) => candidate.id === branch.id)
            );
    }, [props.Contract, props.ContractBranches]);

    const branchGroups = useMemo<BranchGroup[]>(() => {
        // the visits-full payload used for `Stages` does not reliably carry branchId,
        // so resolve it from the authoritative Contract.stages list when available.
        const stageBranchMap = new Map<number, number | null | undefined>(
            (props.Contract?.stages || []).map((stage) => [stage.id, stage.branchId])
        );
        // Tampoco trae la adenda; sin ella el store del contrato no sabe qué etapa actualizar.
        const stageAddendumMap = new Map<number, number | undefined>(
            (props.Contract?.stages || []).map((stage) => [stage.id, stage.contractAddendumId])
        );

        const stages = (props.Stages || []).map((stage) => ({
            ...stage,
            branchId: stageBranchMap.has(stage.id) ? stageBranchMap.get(stage.id) : stage.branchId,
            contractAddendumId: stage.contractAddendumId ?? stageAddendumMap.get(stage.id)
        }));
        const unassignedStages = stages.filter((stage) => stage.branchId === undefined || stage.branchId === null);

        const groupsByBranchId = new Map<number, ContractStage[]>();
        stages.forEach((stage) => {
            if (stage.branchId === undefined || stage.branchId === null) {
                return;
            }
            const stagesForBranch = groupsByBranchId.get(stage.branchId) || [];
            stagesForBranch.push(stage);
            groupsByBranchId.set(stage.branchId, stagesForBranch);
        });

        const knownBranchIds = new Set(contractBranches.map((branch: ContractBranch) => branch.id));
        const orderedBranchIds = [
            ...contractBranches.map((branch: ContractBranch) => branch.id),
            ...Array.from(groupsByBranchId.keys()).filter((branchId) => !knownBranchIds.has(branchId))
        ];

        const branchGroupsList: BranchGroup[] = orderedBranchIds
            .filter((branchId) => groupsByBranchId.has(branchId))
            .map((branchId) => {
                const branch = contractBranches.find((candidate: ContractBranch) => candidate.id === branchId);
                return {
                    key: `branch-${branchId}`,
                    branchId,
                    label: branch?.branchName || t('stageList.props.unknownBranch', { branchId, defaultValue: 'Rama {{branchId}}' }),
                    stages: groupsByBranchId.get(branchId) || []
                };
            });

        // "Sin rama" sólo cuando hay etapas ahí: una sección vacía no aporta.
        return [
            ...(unassignedStages.length > 0 ? [{
                key: UNASSIGNED_BRANCH_KEY,
                branchId: undefined,
                label: t('stageList.props.unassignedBranch', 'Sin rama'),
                stages: unassignedStages
            }] : []),
            ...branchGroupsList
        ];
    }, [props.Stages, props.Contract?.stages, contractBranches, t]);

    const renderStages = (stages: ContractStage[]) => stages.map((stage: ContractStage, index: number) => {
        const excludedVisitIds = stages
            .filter((sibling) => sibling.id !== stage.id && sibling.emrProtocolVisitIdRelated !== undefined)
            .map((sibling) => sibling.emrProtocolVisitIdRelated as number);

        return (
            <StageFixFormItemComponent
                key={`${stage.id}-${index}`}
                ProtocolId={props.ProtocolId}
                StageId={stage.id}
                AddendumId={stage.contractAddendumId}
                ExcludedVisitIds={excludedVisitIds}
            />
        );
    });

    const hasBranchGroups = branchGroups.some((group) => group.branchId !== undefined);
    const summary = summarizeStages(props.Stages);
    const toggle = (key: string) => setCollapsed((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));

    return (
        <GuideDrawer
            open={props.isOpen}
            size="large"
            className="sfx-drawer"
            eyebrow={props.Protocol?.title || props.Protocol?.protocolNumber || undefined}
            title={t('stageFixForm.header', 'Visitas protocolares por etapa')}
            subtitle={t('stageFixForm.summary.description', 'Cada etapa del contrato se asocia a una única visita del flowchart; una misma visita puede repetirse en distintas etapas.')}
            onDismiss={HandleCancelForm}
            closeLabel={t('stageFixForm.actions.close', 'Cerrar')}
            testId="stage-fix-drawer"
            secondaryAction={{ label: t('stageFixForm.actions.close', 'Cerrar') }}
        >
            <div className="sfx-summary" aria-label="Resumen">
                <span className="sfx-pill">{t('stageFixForm.summary.totalStages', { count: summary.total, defaultValue: '{{count}} etapas' })}</span>
                <span className="sfx-tag sfx-tag--ok">{summary.linked} asociadas</span>
                {summary.unlinked > 0 && <span className="sfx-tag sfx-tag--warn">{summary.unlinked} sin visita</span>}
                {props.isLoadingStages && <span className="sfx-muted">Actualizando…</span>}
            </div>

            {summary.total === 0 && !props.isLoadingStages && (
                <div className="sfx-empty">
                    <span className="sfx-empty__title">Todavía no hay etapas</span>
                    <span className="sfx-muted">Las etapas salen del contrato asociado al protocolo.</span>
                </div>
            )}

            {hasBranchGroups ? (
                branchGroups.map((group) => {
                    const isOpen = !collapsed.includes(group.key);
                    const groupSummary = summarizeStages(group.stages);
                    return (
                        <section key={group.key} className={`sfx-group${isOpen ? '' : ' sfx-group--collapsed'}`}>
                            <button type="button" className="sfx-group__head" onClick={() => toggle(group.key)} aria-expanded={isOpen}>
                                <span className="sfx-group__title">{group.label}</span>
                                <span className="sfx-group__count">{group.stages.length}</span>
                                {groupSummary.unlinked > 0 && (
                                    <span className="sfx-tag sfx-tag--warn sfx-group__warn">{groupSummary.unlinked} sin visita</span>
                                )}
                                <ChevronDown16Regular className="sfx-group__chevron" />
                            </button>
                            {isOpen && (
                                <div className="sfx-list">
                                    {group.stages.length > 0
                                        ? renderStages(group.stages)
                                        : <span className="sfx-muted">{t('stageFixForm.branchGroup.empty', 'No hay etapas asignadas a esta rama.')}</span>}
                                </div>
                            )}
                        </section>
                    );
                })
            ) : (
                <div className="sfx-list">
                    {renderStages(props.Stages || [])}
                </div>
            )}
        </GuideDrawer>
    );
};

export { StageFixFormComponent };

export default connect (
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
) (StageFixFormComponent as any);
